import React, { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  Box,
  ChipsToggler,
  FieldWrapper,
  Label,
  Option,
  OptionKey,
  SelectDropdown,
  Text,
} from '@common-fe/common-fe';

import useCronBuilder, {
  AmPm,
  CRON_FREQUENCY_OPTIONS,
  CronDayOfWeek,
  CronFrequency,
} from '@/utils/handlers/useCronBuilder';

import { SETUP_SCHEDULER_FOOTER_PORTAL_ID } from '../ScheduledReport.types';

import AffectedBanner from './AffectedBanner';
type OptionChangeCallback = (value: OptionKey) => void;
interface Props {
  value: string;
  onSetValue: (value: string) => void;
  errorMessage?: string;
  children?: React.ReactNode;
}

export const FREQUENCY_OPTIONS = [
  {
    key: CronFrequency.Weekly,
    value: 'Weekly',
  },
  {
    key: CronFrequency.Monthly,
    value: 'Monthly',
  },
  {
    key: CronFrequency.Quarterly,
    value: 'Quarterly',
  },
  // {
  //   key: CronFrequency.Yearly,
  //   value: 'Yearly',
  // },
];

const WEEK_DAY_OPTIONS: Option[] = [
  {
    key: CronDayOfWeek.Sunday,
    value: 'Sunday',
  },
  {
    key: CronDayOfWeek.Monday,
    value: 'Monday',
  },
  {
    key: CronDayOfWeek.Tuesday,
    value: 'Tuesday',
  },
  {
    key: CronDayOfWeek.Wednesday,
    value: 'Wednesday',
  },
  {
    key: CronDayOfWeek.Thursday,
    value: 'Thursday',
  },
  {
    key: CronDayOfWeek.Friday,
    value: 'Friday',
  },
  {
    key: CronDayOfWeek.Saturday,
    value: 'Saturday',
  },
];
const CronBuilderField: React.FC<Props> = ({ value, onSetValue, errorMessage, children }) => {
  const timeOptions = useMemo(() => {
    const options = CRON_FREQUENCY_OPTIONS.map((key) => {
      const option = {
        key: key,
        value: `${key}:00`,
      };
      return option;
    });
    return options;
  }, []);

  const {
    frequency,
    setFrequency,
    selectedDays,
    setSelectedDay,
    selectedHour,
    setSelectedHour,

    meridiem,
    setMeridiem,
    cronString,
    parseCronString,
  } = useCronBuilder({
    ignoreTimezones: true,
    // timezone: Timezone.EDT,
  });
  useEffect(() => {
    onSetValue(cronString);
  }, [cronString, onSetValue]);

  useEffect(() => {
    if (value) {
      parseCronString(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const footerElement = document.getElementById(SETUP_SCHEDULER_FOOTER_PORTAL_ID);

  return (
    <Box direction="column" margin={{ top: 'spacing16' }}>
      <FieldWrapper align="center">
        <Label name="frequency" margin={{ top: 'spacing6' }} showRequireIcon isNarrow={true}>
          Frequency
        </Label>
        <SelectDropdown
          options={FREQUENCY_OPTIONS}
          value={frequency}
          testId="cron_frequency"
          errorText={errorMessage}
          onChange={setFrequency as OptionChangeCallback}
          singleMode
          id="frequency"
          name="frequency"
        />
      </FieldWrapper>
      {frequency === CronFrequency.Weekly && (
        <FieldWrapper align="start" margin={{ top: 'spacing16' }}>
          <Label name="weekDay" margin={{ top: 'spacing6' }} showRequireIcon isNarrow={true}>
            Day of week
          </Label>
          <SelectDropdown
            options={WEEK_DAY_OPTIONS}
            value={selectedDays[0]}
            onChange={setSelectedDay as OptionChangeCallback}
            singleMode
            testId="cron_week-day"
            id="weekDay"
            name="weekDay"
          />
        </FieldWrapper>
      )}
      {footerElement ? createPortal(<AffectedBanner value={frequency} />, footerElement) : null}
      {children}

      <FieldWrapper align="start" margin={{ top: 'spacing16' }}>
        <Label name="time" isNarrow={true} pad={{ right: 'spacing8' }} margin={{ right: '0' }}>
          <Box direction="column">
            <Box direction="row">
              <Text size="medium" weight={700}>
                Time to run report
              </Text>
              <Text size="medium" color="textSecondary" weight={400} margin={{ left: 'spacing4' }}>
                (ET)
              </Text>
            </Box>
            <Text size="small" weight={500} color="textSecondary" margin={{ top: 'spacing2' }}>
              The report will be generated within an hour from the selected time, and admins will be notified
              via email.
            </Text>
          </Box>
        </Label>
        <Box direction="row">
          <Box width={{ max: '160px', width: '160px' }}>
            <SelectDropdown
              options={timeOptions}
              id="time"
              name="time"
              testId="cron_time"
              singleMode
              value={selectedHour}
              onChange={setSelectedHour as OptionChangeCallback}
            />
          </Box>
          <Box direction="row" margin={{ left: 'spacing12' }}>
            <ChipsToggler
              testId="cron_ampm"
              options={[
                {
                  key: AmPm.AM,
                  value: 'AM',
                },
                {
                  key: AmPm.PM,
                  value: 'PM',
                },
              ]}
              value={meridiem}
              onChange={function (value: string): void {
                setMeridiem(value as AmPm);
              }}
            />
          </Box>
        </Box>
      </FieldWrapper>
      {/* {cronString} */}
    </Box>
  );
};

export default React.memo(CronBuilderField);
